/**
 * The six review sections, rendered READ-ONLY.
 *
 * The approved prototype made this a six-step editing wizard: 57 inputs, 21 chip groups, a
 * "Confirm all as correct" write. v1 has no write except "mark as given" for worming and flea, so
 * every one of those controls is rendered here as text. The layout, the section order, the kicker /
 * question / help stack and the two-column `.ft-grid-form` are kept exactly — what changed is that
 * nothing pretends to be editable.
 *
 * TWO PROPERTIES THIS FILE EXISTS TO DEFEND
 * -----------------------------------------
 *  1. **Nothing is spelled out.** The prototype wrote "Milo" into its step questions and help text.
 *     Shipped as-is that shows every client someone else's dog's name. Every question and help
 *     string here is a function of the dog's name, and every value comes off the payload.
 *  2. **Absence is designed, not blank.** Against the 199 live records most sections are partly or
 *     wholly empty — the care date fields were only added on 2026-07-30, and a meal record is only
 *     confidently linked for a minority of dogs. A row with nothing behind it says so; a section
 *     with nothing behind it gets a card that explains what is missing and how to fix it. There is
 *     no path here that can render "undefined", "NaN" or "Invalid Date".
 *
 * All derivation is @ft/shared's. Nothing in this file computes a due date, a portion or an
 * interval — the server did that, and a second implementation would drift from it.
 */

import type { CSSProperties, ReactElement, ReactNode } from 'react'
import {
  ageLabel,
  fmtLongDay,
  parseDay,
  parseWeight,
  type CareItem,
  type DogPayload,
  type LinkMethod,
  type Maybe,
  type OwnerDetails,
} from '@ft/shared'

import { CARE_ICON_PATHS, Icon, UI_ICON_PATHS, careIconFor } from '../model/icons'
import type { StepId } from '../state/portal'

// ── Reading a value off the record ──────────────────────────────────────────────────────────────

/**
 * A trimmed value, or null when the record genuinely holds nothing.
 *
 * The literal strings "undefined" and "null" are filtered because they are what a stringified
 * absence looks like by the time it has been through a form backend, and they read to a client as
 * a broken page rather than as an empty field.
 */
function clean(value: Maybe<string> | undefined): string | null {
  if (value == null) return null
  const trimmed = value.trim()
  if (trimmed === '') return null
  const lowered = trimmed.toLowerCase()
  return lowered === 'undefined' || lowered === 'null' ? null : trimmed
}

/**
 * A date for display.
 *
 * Unparseable text is returned AS WRITTEN rather than dropped: "sometime last spring" is the
 * client's own answer and hiding it would be a lie about what we hold. `parseDay` never yields an
 * Invalid Date, so nothing downstream can render one.
 */
function dayText(value: Maybe<string> | undefined): string | null {
  const raw = clean(value)
  const parsed = parseDay(raw)
  return parsed ? fmtLongDay(parsed) : raw
}

// ── Shared presentation ─────────────────────────────────────────────────────────────────────────

/**
 * The design system styles `.field > label`, and there is no `<label>` here because there is no
 * control to caption — a label element pointing at nothing is a lie to a screen reader. So the
 * rows are a real definition list and the term carries the design system's own label recipe,
 * token for token. This is the one place it is restated; do not re-type it elsewhere.
 */
const TERM_STYLE: CSSProperties = {
  display: 'block',
  fontSize: '12px',
  marginBottom: '5px',
  color: 'color-mix(in srgb, var(--color-text) 70%, transparent)',
}

const VALUE_STYLE: CSSProperties = {
  display: 'block',
  fontSize: '15px',
  fontWeight: 600,
  lineHeight: 1.45,
  color: 'var(--color-text)',
  // Microchip numbers, policy numbers and long emails must wrap rather than push the grid wider
  // than the card on a 320px screen.
  overflowWrap: 'anywhere',
}

/** Absence is a real answer, so it is typeset as one — quieter than a value, never blank. */
const MISSING_STYLE: CSSProperties = {
  ...VALUE_STYLE,
  fontSize: '14px',
  color: 'var(--color-neutral-600)',
}

const HINT_STYLE: CSSProperties = {
  display: 'block',
  fontSize: '12.5px',
  lineHeight: 1.45,
  marginTop: '4px',
}

const WIDE_ROW: CSSProperties = { gridColumn: '1 / -1' }

const NOTE_BOX: CSSProperties = {
  display: 'flex',
  gap: '10px',
  alignItems: 'flex-start',
  marginTop: '18px',
  padding: '14px 16px',
  background: 'var(--color-neutral-100)',
  border: '1px solid var(--color-divider)',
  borderRadius: 'calc(var(--radius-lg) * 1.15)',
}

const SUB_HEAD: CSSProperties = {
  marginTop: '18px',
  paddingTop: '16px',
  borderTop: '1px solid var(--color-divider)',
}

const NOT_RECORDED = 'Not recorded'

export interface ReadRow {
  readonly label: string
  /** `null` means the record holds nothing — rendered as a stated absence, never as a blank. */
  readonly value: string | null
  /** A quieter second line: why the value looks like it does, or what to do about it. */
  readonly hint?: string
  /** Spans both columns at ≥760px. For addresses, notes and anything that runs long. */
  readonly wide?: boolean
  /** Wording for the absence when "Not recorded" is not specific enough to be useful. */
  readonly missing?: string
}

function ReadRowView({ label, value, hint, wide, missing }: ReadRow): ReactElement {
  const present = value !== null && value !== ''
  return (
    <div style={wide === true ? WIDE_ROW : undefined}>
      <dt style={TERM_STYLE}>{label}</dt>
      <dd style={{ margin: 0 }}>
        <span style={present ? VALUE_STYLE : MISSING_STYLE}>{present ? value : (missing ?? NOT_RECORDED)}</span>
        {hint === undefined ? null : (
          <span className="text-muted" style={HINT_STYLE}>
            {hint}
          </span>
        )}
      </dd>
    </div>
  )
}

/**
 * A grid of read-only rows, or the section's empty state when the record holds nothing at all.
 *
 * THE GRID TRAP: each row is ONE `<div>` wrapping its `<dt>`/`<dd>`. A bare dt+dd pair would make
 * every row TWO children of `.ft-grid-form`, which is `grid-template-columns:1fr 1fr` above 760px —
 * the terms would file down one column and the values down the other. This is the same class of
 * defect as wrapping a conditional grid child, and no compiler sees it.
 */
function RowGrid({ rows, empty }: { readonly rows: readonly ReadRow[]; readonly empty: ReactNode }): ReactElement {
  const anything = rows.some((row) => row.value !== null && row.value !== '')
  if (!anything) return <>{empty}</>
  return (
    <dl className="ft-grid-form" style={{ margin: 0 }}>
      {rows.map((row) => (
        <ReadRowView key={row.label} {...row} />
      ))}
    </dl>
  )
}

export interface EmptyStateProps {
  readonly icon: readonly string[]
  readonly title: string
  readonly body: string
  /** An optional action — the only interactive thing an empty state ever carries. */
  readonly children?: ReactNode
}

/**
 * The designed absence.
 *
 * Organic has no red/amber/green ramp and an empty section is not an error, so this uses the calm
 * accent-2 family throughout. It always says two things: what is missing, and how it gets fixed.
 */
export function EmptyState({ icon, title, body, children }: EmptyStateProps): ReactElement {
  return (
    <div
      style={{
        display: 'grid',
        justifyItems: 'center',
        textAlign: 'center',
        gap: '6px',
        padding: '26px 20px',
        background: 'var(--color-neutral-100)',
        border: '1px solid var(--color-divider)',
        borderRadius: 'calc(var(--radius-lg) * 1.15)',
      }}
    >
      <span
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--color-accent-2-100)',
          color: 'var(--color-accent-2-800)',
          marginBottom: '6px',
        }}
      >
        <Icon paths={icon} size={22} />
      </span>
      <strong style={{ fontSize: '16px', lineHeight: 1.3 }}>{title}</strong>
      <p className="text-muted" style={{ margin: 0, maxWidth: '46ch', fontSize: '13.5px', lineHeight: 1.55 }}>
        {body}
      </p>
      {children}
    </div>
  )
}

// ── Care rows (health & treatments) ─────────────────────────────────────────────────────────────

/**
 * One treatment, exactly as the server computed it.
 *
 * `status`, `detail`, `percent` and `urgency` are all read straight off the item. Urgency is the
 * only colour decision, and Organic expresses it as accent (needs attention) vs accent-2 (fine) —
 * there is no red/amber/green to reach for.
 */
function CareRow({ item }: { readonly item: CareItem }): ReactElement {
  const calm = item.urgency === 'calm'
  return (
    <li
      style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        padding: '14px 0',
        borderTop: '1px solid var(--color-divider)',
      }}
    >
      <span
        style={{
          width: '38px',
          height: '38px',
          flex: 'none',
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          background: calm ? 'var(--color-accent-2-100)' : 'var(--color-accent-100)',
          color: calm ? 'var(--color-accent-2-700)' : 'var(--color-accent-700)',
        }}
      >
        <Icon paths={careIconFor(item.kind)} size={18} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
          <strong style={{ fontSize: '14.5px' }}>{item.name}</strong>
          {item.hasStatus ? (
            <span className={calm ? 'tag tag-accent-2' : 'tag tag-accent'} style={{ flex: 'none' }}>
              {item.status}
            </span>
          ) : null}
        </span>
        <span className="text-muted" style={{ display: 'block', fontSize: '13px', marginTop: '4px', lineHeight: 1.5 }}>
          {item.needsInterval
            ? 'We have the last date but not how often it is repeated, so we cannot work out the next one yet.'
            : item.detail}
        </span>
      </span>
    </li>
  )
}

// ── Section bodies ──────────────────────────────────────────────────────────────────────────────

export interface SectionBodyProps {
  readonly dog: DogPayload
  readonly owner: OwnerDetails
  /**
   * The SERVER's date, pinned to midday. Used for presentation only — an age label. Due dates
   * arrive already computed; a client clock is never allowed to decide when something is due.
   */
  readonly today: Date
  /**
   * The portal's one write lives on the Reminders screen. Health & treatments points at it rather
   * than growing a second copy of the control, so there is one place where a treatment is recorded
   * and one place where its wording and its failure handling live.
   */
  readonly goReminders: () => void
}

function BasicsBody({ dog, today }: SectionBodyProps): ReactElement {
  const identity = dog.record.identity
  const dob = parseDay(identity.dateOfBirth)
  const weight = parseWeight(identity.weightRaw)
  const age = ageLabel(dob, today)
  const microchip = clean(identity.microchip)

  const rows: readonly ReadRow[] = [
    { label: 'Name', value: clean(identity.name) ?? dog.name },
    { label: 'Breed', value: clean(identity.breed) },
    {
      label: 'Date of birth',
      value: dayText(identity.dateOfBirth),
      ...(identity.dobIsApprox ? { hint: 'Recorded as an estimate.' } : {}),
    },
    { label: 'Age', value: age === '' ? null : age, missing: 'We need a date of birth for this' },
    {
      label: 'Weight',
      value: weight.raw === '' ? null : weight.display,
      // parseWeight refuses to guess a number out of prose or a range, and says so rather than
      // concatenating "kg" onto whatever was typed. 5% of live values are free text.
      ...(weight.raw !== '' && weight.kg === null
        ? { hint: 'Held exactly as it was written down — tell us the number in kg and we will tidy it.' }
        : {}),
    },
    {
      label: 'Microchip number',
      value: microchip,
      missing: 'Not on file',
      // Owner rule: every dog with us must have a microchip. There is no "not chipped yet" wording
      // here on purpose — the absence is stated and the fix is asked for.
      ...(microchip === null
        ? { hint: 'Every dog with us needs a microchip number. Please bring it to your next visit.' }
        : {}),
    },
    { label: 'Sex', value: clean(identity.sex) },
    { label: 'Neutered or spayed', value: clean(identity.neutered) },
    { label: 'Age when neutered or spayed', value: clean(identity.neuterAge) },
    { label: 'Size', value: clean(identity.size) },
    { label: `Age when you got ${dog.name}`, value: clean(identity.ageWhenAcquired) },
    { label: `Where ${dog.name} came from`, value: clean(identity.source) },
    { label: 'Description', value: clean(identity.description), wide: true },
    { label: 'Specific markings', value: clean(identity.markings), wide: true },
  ]

  return (
    <RowGrid
      rows={rows}
      empty={
        <EmptyState
          icon={UI_ICON_PATHS.message}
          title={`We have almost nothing on ${dog.name} yet`}
          body="Bring the basics to your next visit — date of birth, breed, weight and the microchip number — and we will fill this in with you."
        />
      }
    />
  )
}

function TreatmentsBody({ dog, goReminders }: SectionBodyProps): ReactElement {
  // Feeding and the contact review are care items too, but they belong to other sections; this one
  // is the four treatments.
  const treatments = dog.care.filter(
    (item) => item.kind === 'worming' || item.kind === 'flea' || item.kind === 'booster' || item.kind === 'kennel',
  )
  const canMark = treatments.some((item) => item.selfAdministered)
  const records = dog.record.treatments

  const extraRows: readonly ReadRow[] = [
    { label: 'Worming product', value: clean(records.wormingBrand) },
    { label: 'Flea product', value: clean(records.fleaBrand) },
    { label: 'Last booster given', value: dayText(records.boosterLastGiven) },
    { label: 'Kennel cough last given', value: dayText(records.kennelCoughGiven) },
  ]
  const hasExtra = extraRows.some((row) => row.value !== null)

  if (treatments.length === 0) {
    return (
      <>
        <EmptyState
          icon={CARE_ICON_PATHS.shield}
          title={`No treatment dates for ${dog.name} yet`}
          body="We only started holding worming, flea, booster and kennel cough dates recently, so most dogs have none on file. Bring the dates — or the packaging — to your next visit and we will add them. Your reminders start working the moment we do."
        />
        {hasExtra ? (
          <div style={SUB_HEAD}>
            <div className="card-kicker" style={{ fontSize: '11px' }}>
              What we do hold
            </div>
            <div style={{ marginTop: '12px' }}>
              <RowGrid rows={extraRows} empty={null} />
            </div>
          </div>
        ) : null}
      </>
    )
  }

  return (
    <>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {treatments.map((item) => (
          <CareRow key={item.kind} item={item} />
        ))}
      </ul>

      {canMark ? (
        <div style={NOTE_BOX}>
          <Icon
            paths={UI_ICON_PATHS.check}
            size={16}
            stroke="var(--color-accent-700)"
            style={{ flex: 'none', marginTop: '2px' }}
          />
          <div>
            <strong style={{ display: 'block', fontSize: '14px' }}>Treated {dog.name} yourself?</strong>
            {/* Booster and kennel cough are deliberately absent from this sentence AND from the
                API type: they are vet-issued compliance artefacts the business's licence depends
                on, so they can only move when a certificate does. */}
            <p className="text-muted" style={{ margin: '4px 0 10px', fontSize: '13px', lineHeight: 1.5 }}>
              Worming and flea are yours to give, so you can record them yourself on the reminders screen and the next
              date moves on its own. Booster and kennel cough come from your vet, so we update those from the
              certificate.
            </p>
            <button className="btn btn-secondary" onClick={goReminders} style={{ minHeight: '44px' }}>
              Mark a treatment as given
              <Icon paths={UI_ICON_PATHS.chevronRight} size={15} />
            </button>
          </div>
        </div>
      ) : null}

      {hasExtra ? (
        <div style={SUB_HEAD}>
          <div className="card-kicker" style={{ fontSize: '11px' }}>
            What we do hold
          </div>
          <div style={{ marginTop: '12px' }}>
            <RowGrid rows={extraRows} empty={null} />
          </div>
        </div>
      ) : null}
    </>
  )
}

function MedicalBody({ dog }: SectionBodyProps): ReactElement {
  const medical = dog.record.medical

  const rows: readonly ReadRow[] = [
    { label: 'Vet practice', value: clean(medical.vetPractice) },
    { label: 'Vet phone', value: clean(medical.vetPhone) },
    { label: 'Vet practice address', value: clean(medical.vetAddress), wide: true },
    { label: 'Pet insurance', value: clean(medical.insured) },
    { label: 'Insurance provider', value: clean(medical.insurer) },
    { label: 'Policy number', value: clean(medical.policyNumber) },
    { label: 'Insurance renewal', value: dayText(medical.insuranceExpiry) },
    { label: 'On medication', value: clean(medical.onMedication) },
    { label: 'Medication, type and dosage', value: clean(medical.medicationDetail), wide: true },
    { label: 'Health issues or notes', value: clean(medical.healthIssues), wide: true },
    { label: 'Kennel cough vaccination received', value: clean(medical.kennelCoughReceived) },
    { label: 'Vaccinations up to date', value: clean(medical.vaccinationsUpToDate) },
  ]

  return (
    <RowGrid
      rows={rows}
      empty={
        <EmptyState
          icon={UI_ICON_PATHS.message}
          title={`No vet or medical details for ${dog.name}`}
          body="Who your vet is, whether there is insurance, and anything we should watch for — these are what let us act fast if something happens on a day with us. Tell us at your next visit and we will add them."
        />
      }
    />
  )
}

/** How the feeding plan was matched to this dog. Rendered so a client can challenge a wrong one. */
const LINK_NOTE: Readonly<Record<LinkMethod, string>> = {
  'Portal-prefill': 'From the feeding form you filled in yourself.',
  'Human-confirmed': 'Checked by our team against your dog.',
  'Exact match (dog+surname)': 'Matched on your dog’s name and your surname — we will confirm it with you.',
  Unmatched: 'Not matched to your dog yet.',
  Disputed: 'We think this may belong to another dog, so we are checking it.',
}

function FoodBody({ dog }: SectionBodyProps): ReactElement {
  const food = dog.food

  // A meal record is only shown when its link to THIS dog has been confirmed. Dog name + parent
  // surname resolves 62% of meal rows and 45% carry a dog name unknown to the registration form,
  // so a best guess here is how one client ends up reading another dog's feeding instructions.
  if (food.state === 'unconfirmed') {
    return (
      <EmptyState
        icon={CARE_ICON_PATHS.bone}
        title={`We have a feeding plan, but not one we can prove is ${dog.name}’s`}
        body="Feeding forms do not always carry enough to tie them to the right dog, and showing you the wrong plan would be worse than showing none. We will confirm it with you at your next visit and it will appear here."
      />
    )
  }

  if (food.state === 'missing') {
    return (
      <EmptyState
        icon={CARE_ICON_PATHS.bone}
        title={`No feeding plan on file for ${dog.name}`}
        body="Tell us how many meals a day, what type of food, and how much each day — in cups, scoops, handfuls, sachets or tins — and the team will follow it at every visit."
      />
    )
  }

  const plan = food.plan
  const rows: readonly ReadRow[] = [
    { label: 'Meals a day', value: plan.dailyLine },
    { label: 'Food', value: plan.typeLine === 'Food not recorded' ? null : plan.typeLine },
    {
      label: 'Daily amount',
      value: plan.portionUnknown ? null : plan.dailyPortion,
      missing: 'No amount recorded',
      ...(plan.portionUnknown
        ? { hint: `Tell us how much ${dog.name} has each day and we will follow it exactly.` }
        : {}),
    },
    { label: 'Supplements', value: plan.supplementLine === '' ? null : plan.supplementLine },
    { label: 'Medication at mealtimes', value: plan.medicationLine === '' ? null : plan.medicationLine },
    { label: 'Medication details', value: plan.medicationDetail ?? null, wide: true },
    { label: 'Anything else', value: plan.notes, wide: true },
  ]

  return (
    <>
      <dl className="ft-grid-form" style={{ margin: 0 }}>
        {rows.map((row) => (
          <ReadRowView key={row.label} {...row} />
        ))}
      </dl>

      <div style={SUB_HEAD}>
        <div className="card-kicker" style={{ fontSize: '11px' }}>
          At daycare
        </div>
        {/* Mealtimes are the BUSINESS's, not the dog's — the per-dog "what times?" question was
            withdrawn deliberately. Saying so stops a client reading these as their own timings. */}
        <p className="text-muted" style={{ margin: '8px 0 0', fontSize: '13px', lineHeight: 1.5 }}>
          These are our mealtimes, and we split {dog.name}’s meals across them.
        </p>
        <ol style={{ listStyle: 'none', margin: '14px 0 0', padding: 0, display: 'grid', gap: '12px' }}>
          {plan.slots.map((slot) => (
            <li key={slot.name}>
              <strong style={{ minWidth: 0, fontSize: '14px' }}>{slot.name}</strong>
            </li>
          ))}
        </ol>
      </div>

      <p className="text-muted" style={{ margin: '14px 0 0', fontSize: '12.5px', lineHeight: 1.5 }}>
        {LINK_NOTE[food.linkMethod]}
      </p>
    </>
  )
}

function BehaviourBody({ dog }: SectionBodyProps): ReactElement {
  const behaviour = dog.record.behaviour

  const rows: readonly ReadRow[] = [
    { label: 'Recall off the lead', value: clean(behaviour.recall) },
    { label: 'Flight risk', value: clean(behaviour.flightRisk) },
    { label: 'Food possessive', value: clean(behaviour.foodPossessive) },
    { label: 'Guards toys or space', value: clean(behaviour.resourceGuarding) },
    { label: 'Training and obedience', value: clean(behaviour.obedience) },
    { label: 'With other dogs', value: clean(behaviour.withOtherDogs) },
    { label: 'Has growled at someone', value: clean(behaviour.hasGrowled) },
    { label: 'Has bitten someone', value: clean(behaviour.hasBitten) },
    { label: 'Crate trained', value: clean(behaviour.crateTrained) },
    { label: 'House trained', value: clean(behaviour.houseTrained) },
    { label: 'Enjoys people', value: clean(behaviour.likesHumanInteraction) },
    { label: 'Chews things', value: clean(behaviour.chewsObjects) },
    { label: 'Where they sleep', value: clean(behaviour.sleepsWhere) },
    { label: 'How they ask to go out', value: clean(behaviour.toiletIndication) },
    { label: 'Anything else we should watch for', value: clean(behaviour.riskyBehaviours), wide: true },
  ]

  return (
    <RowGrid
      rows={rows}
      empty={
        <EmptyState
          icon={UI_ICON_PATHS.message}
          title={`Nothing recorded about how ${dog.name} gets on`}
          body="Recall, other dogs, guarding, whether anything has ever gone wrong — honest answers are what let us put your dog in the right group rather than guessing. Tell us at your next visit."
        />
      }
    />
  )
}

function DetailsBody({ owner }: SectionBodyProps): ReactElement {
  const rows: readonly ReadRow[] = [
    { label: 'Your name', value: clean(owner.name) },
    { label: 'Email', value: clean(owner.email) },
    { label: 'Mobile phone', value: clean(owner.mobile) },
    { label: 'Home phone', value: clean(owner.homePhone) },
    { label: 'Work phone', value: clean(owner.workPhone) },
    { label: 'Home address', value: clean(owner.address), wide: true },
    { label: 'Postcode', value: clean(owner.postcode) },
  ]

  const partnerRows: readonly ReadRow[] = [
    { label: 'Name', value: clean(owner.partnerName) },
    { label: 'Mobile phone', value: clean(owner.partnerMobile) },
    { label: 'Email', value: clean(owner.partnerEmail), wide: true },
  ]
  const hasPartner = partnerRows.some((row) => row.value !== null)

  return (
    <>
      <RowGrid
        rows={rows}
        empty={
          <EmptyState
            icon={UI_ICON_PATHS.message}
            title="We have no contact details for you"
            body="This is the one section we really cannot work without — if we cannot reach you on a day your dog is with us, we cannot tell you anything. Give us a number at your next visit."
          />
        }
      />

      <div style={SUB_HEAD}>
        <div className="card-kicker" style={{ fontSize: '11px' }}>
          Second parent or partner
        </div>
        <div style={{ marginTop: '12px' }}>
          {hasPartner ? (
            <RowGrid rows={partnerRows} empty={null} />
          ) : (
            <p className="text-muted" style={{ margin: 0, fontSize: '13.5px', lineHeight: 1.55 }}>
              Nobody else on your record. If there is someone we can call when you are not reachable, tell us and we
              will add them.
            </p>
          )}
        </div>
      </div>

      {/* The emergency contact is a third party who never filled in a form and never saw a privacy
          notice — roughly 156 of them are in the table. It is a separate, audited endpoint and is
          deliberately NOT rendered here; the honest thing is to say we hold it, not to show it. */}
      <div style={NOTE_BOX}>
        <Icon
          paths={CARE_ICON_PATHS.phone}
          size={16}
          stroke="var(--color-accent-700)"
          style={{ flex: 'none', marginTop: '2px' }}
        />
        <div>
          <strong style={{ display: 'block', fontSize: '14px' }}>Emergency contact</strong>
          <p className="text-muted" style={{ margin: '4px 0 0', fontSize: '13px', lineHeight: 1.5 }}>
            We hold this separately and keep it off the page, because the person you named never filled in a form with
            us themselves. Ask us any time and we will check it with you.
          </p>
        </div>
      </div>
    </>
  )
}

// ── The six sections ────────────────────────────────────────────────────────────────────────────

export type ReviewSectionId = 'basics' | 'treatments' | 'medical' | 'food' | 'behaviour' | 'details'

export interface ReviewSection {
  readonly id: ReviewSectionId
  /** The sidebar's wording and the card's kicker. */
  readonly title: string
  /**
   * `question` and `help` are FUNCTIONS OF THE DOG'S NAME, not strings.
   *
   * The prototype hard-coded "Milo" into all six questions and four of the help lines. A literal
   * name in this file is the defect that shows every client someone else's dog.
   */
  readonly question: (name: string) => string
  readonly help: (name: string) => string
  readonly Body: (props: SectionBodyProps) => ReactElement
}

/**
 * Keyed by `StepId` rather than held in an array, so a step that exists in the union but has no
 * section is a compile error rather than a blank card. The count the screen prints comes from
 * `STEP_COUNT`, never from a literal — a wizard that says "of 6" while rendering five sections is
 * the exact defect the prototype shipped.
 */
export const REVIEW_SECTIONS: Readonly<Record<StepId, ReviewSection>> = {
  0: {
    id: 'basics',
    title: 'The basics',
    question: (name) => `Is this still ${name}?`,
    help: (name) => `What we hold on ${name}’s daycare record.`,
    Body: BasicsBody,
  },
  1: {
    id: 'treatments',
    title: 'Health & treatments',
    question: (name) => `Are ${name}’s treatment dates still right?`,
    help: () => 'We use these dates to time your reminders.',
    Body: TreatmentsBody,
  },
  2: {
    id: 'medical',
    title: 'Medical & vet',
    question: (name) => `Are ${name}’s medical details up to date?`,
    help: () => 'Vet, insurance and any health needs — so we can act quickly if anything comes up.',
    Body: MedicalBody,
  },
  3: {
    id: 'food',
    title: 'Food & routine',
    question: (name) => `How is ${name} eating at the moment?`,
    help: () => 'The team follows this routine at every visit.',
    Body: FoodBody,
  },
  4: {
    id: 'behaviour',
    title: 'Behaviour',
    question: (name) => `How is ${name} out and about?`,
    help: (name) => `This is what helps us put ${name} with the right group.`,
    Body: BehaviourBody,
  },
  5: {
    id: 'details',
    title: 'Your details',
    question: () => 'How do we reach you?',
    help: () => 'Your contact details, and who else we can call.',
    Body: DetailsBody,
  },
}
